/**
 * Criar ou editar um plano do catálogo (#766), no painel lateral, como os
 * outros formulários de criar e editar: o catálogo continua à vista.
 *
 * Os valores são o preço PADRÃO. A nota no topo diz o que a pessoa mais
 * precisa saber antes de mexer: o preço é copiado para o contrato no dia da
 * contratação, e mudar o plano não muda o de quem já contratou.
 *
 * Só desenha e lê o formulário; quem monta manda para o servidor.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import type { CriarPlano, EditarPlano } from '@cliqcrm/contracts';
import { Botao, Campo, CampoArea, Dialogo, Interruptor } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import { mascararReais } from '@/shared/lib/formato';
import {
  CAMPOS_DE_PLANO_NOVO,
  LIMITE_DA_OBSERVACAO,
  LIMITE_DO_NOME,
  camposDoPlano,
  lerEdicaoDoPlano,
  lerPlanoNovo,
  mudouAlgo,
  podeAtivar,
  type CamposDoPlano,
  type ErrosDoPlano,
} from '../lib/formulario-do-plano';
import type { PlanoDoCatalogo } from '../model/plano-do-catalogo';
import './dialogo-do-plano.css';

export type DialogoDoPlanoProps = {
  /** O plano a editar; nulo para o plano novo. */
  plano: PlanoDoCatalogo | null;
  /** O pedido foi para o servidor e a resposta ainda não voltou. */
  enviando: boolean;
  /** O motivo da recusa do servidor, já em frase. */
  falha: string | null;
  aoCriar: (corpo: CriarPlano) => void;
  aoEditar: (corpo: EditarPlano) => void;
  aoFechar: () => void;
};

const ID_DO_FORMULARIO = 'formulario-do-plano';
const DICA_DO_ATIVO =
  'Plano inativo não aparece para organização nova. Quem já tem continua com ele.';

export function DialogoDoPlano({
  plano,
  enviando,
  falha,
  aoCriar,
  aoEditar,
  aoFechar,
}: DialogoDoPlanoProps): ReactNode {
  const [original] = useState<CamposDoPlano>(() =>
    plano ? camposDoPlano(plano) : CAMPOS_DE_PLANO_NOVO,
  );
  const [campos, definirCampos] = useState<CamposDoPlano>(original);
  const [erros, definirErros] = useState<ErrosDoPlano>({});
  const editando = plano !== null;
  const temMudanca = !editando || mudouAlgo(original, campos);

  function mudar<C extends keyof CamposDoPlano>(campo: C, valor: CamposDoPlano[C]): void {
    definirCampos((atuais) => ({ ...atuais, [campo]: valor }));
    definirErros((atuais) => ({ ...atuais, [campo]: undefined }));
  }

  function enviar(evento: FormEvent): void {
    evento.preventDefault();
    if (editando) {
      const leitura = lerEdicaoDoPlano(original, campos);
      if (leitura.ok) aoEditar(leitura.corpo);
      else definirErros(leitura.erros);
      return;
    }
    const leitura = lerPlanoNovo(campos);
    if (leitura.ok) aoCriar(leitura.corpo);
    else definirErros(leitura.erros);
  }

  const texto = (campo: Exclude<keyof CamposDoPlano, 'ativo'>) => ({
    value: campos[campo],
    erro: erros[campo],
    onChange: (evento: { currentTarget: { value: string } }) =>
      mudar(campo, evento.currentTarget.value),
  });
  // Os preços, com a máscara de reais: "250" vira "2,50".
  const reais = (campo: 'mensalidade' | 'usuarioAdicional' | 'gbAdicional') => ({
    ...texto(campo),
    onChange: (evento: { currentTarget: { value: string } }) =>
      mudar(campo, mascararReais(evento.currentTarget.value)),
  });

  return (
    <Dialogo
      titulo={editando ? `Editar o plano ${plano.nome}` : 'Novo plano'}
      forma="lateral"
      aoFechar={() => {
        if (!enviando) aoFechar();
      }}
      botoes={
        <>
          <Botao variante="fantasma" onClick={aoFechar} desabilitado={enviando} motivo="Salvando…">
            Cancelar
          </Botao>
          <Botao
            type="submit"
            form={ID_DO_FORMULARIO}
            variante="primario"
            desabilitado={enviando || !temMudanca}
            motivo={enviando ? 'Salvando…' : 'Mude algum campo para salvar.'}
          >
            {enviando ? 'Salvando…' : editando ? 'Salvar' : 'Criar plano'}
          </Botao>
        </>
      }
    >
      <form id={ID_DO_FORMULARIO} className="dialogo-do-plano" onSubmit={enviar} noValidate>
        <p className="dialogo-do-plano__nota">
          São os valores padrão. No dia da contratação eles são copiados para o contrato da
          organização, e mudar o plano depois não muda o preço de quem já contratou.
        </p>
        {falha && <Faixa tom="bloqueado">{falha}</Faixa>}

        <Campo
          rotulo="Nome do plano"
          type="text"
          maxLength={LIMITE_DO_NOME}
          autoFocus
          placeholder="Ex.: Essencial"
          {...texto('nome')}
        />

        <fieldset className="dialogo-do-plano__grupo">
          <legend className="dialogo-do-plano__legenda">O que inclui</legend>
          <div className="dialogo-do-plano__linha">
            <Campo
              rotulo="Usuários incluídos"
              inputMode="numeric"
              {...texto('usuariosIncluidos')}
            />
            <Campo rotulo="Armazenamento (GB)" inputMode="numeric" {...texto('armazenamentoGb')} />
          </div>
        </fieldset>

        <fieldset className="dialogo-do-plano__grupo">
          <legend className="dialogo-do-plano__legenda">Preço padrão, em reais</legend>
          <Campo
            rotulo="Mensalidade"
            inputMode="decimal"
            placeholder="0,00"
            {...reais('mensalidade')}
          />
          <div className="dialogo-do-plano__linha">
            <Campo
              rotulo="Usuário adicional"
              inputMode="decimal"
              placeholder="0,00"
              ajuda="Por usuário, por mês."
              {...reais('usuarioAdicional')}
            />
            <Campo
              rotulo="GB adicional"
              inputMode="decimal"
              placeholder="0,00"
              ajuda="Por GB, por mês."
              {...reais('gbAdicional')}
            />
          </div>
        </fieldset>

        <CampoArea
          rotulo="Observação"
          ajuda="Uma linha a mais no cartão do plano. Opcional."
          maxLength={LIMITE_DA_OBSERVACAO}
          rows={2}
          placeholder="Ex.: Inclui a funcionalidade Clínica"
          {...texto('observacao')}
        />

        {editando && (
          <Interruptor
            rotulo="Plano ativo"
            descricao={DICA_DO_ATIVO}
            ligado={campos.ativo}
            desabilitado={!campos.ativo && !podeAtivar(campos)}
            motivo="Preencha os usuários incluídos e os preços para ativar o plano."
            aoAlternar={() => mudar('ativo', !campos.ativo)}
          />
        )}
      </form>
    </Dialogo>
  );
}
